import { useState, useEffect } from 'react';
import axiosInstance from '../lib/axios';

interface SkuValidationOptions {
  sku: string;
  excludeProductId?: string;
  debounceMs?: number;
  enabled?: boolean; // Control when to run validation
}

interface SkuValidationResult {
  isAvailable: boolean | null; // null = not checked yet
  isChecking: boolean;
  error: string | null;
}

/**
 * Custom hook for real-time SKU availability validation
 *
 * @param options - Configuration options
 * @returns Validation state and results
 */
export function useSkuValidation({
  sku,
  excludeProductId,
  debounceMs = 500,
  enabled = true,
}: SkuValidationOptions): SkuValidationResult {
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset state if SKU is empty or validation is disabled
    if (!enabled || !sku || sku.trim().length < 3) {
      setIsAvailable(null);
      setIsChecking(false);
      setError(null);
      return;
    }

    const timer = setTimeout(async () => {
      setIsChecking(true);
      setError(null);

      try {
        const params: any = { sku };
        if (excludeProductId) {
          params.excludeProductId = excludeProductId;
        }

        const response = await axiosInstance.get('/products/sku/check-availability', {
          params,
        });

        setIsAvailable(response.data.available);
      } catch (err: any) {
        console.error('SKU validation error:', err);
        setError(err.response?.data?.message || 'Failed to check SKU availability');
        setIsAvailable(null);
      } finally {
        setIsChecking(false);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [sku, excludeProductId, debounceMs, enabled]);

  return { isAvailable, isChecking, error };
}
